import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link } from 'react-router-dom';
import styled from 'styled-components';
import { 
  motion, 
  useScroll, 
  useTransform,
  AnimatePresence
} from 'framer-motion';
import { 
  FaArrowLeft,
  FaArrowRight,
  FaCode,
  FaMobile,
  FaVrCardboard,
  FaRobot,
  FaWordpress,
  FaGraduationCap,
  FaShoppingCart,
  FaDesktop,
  FaCheck,
  FaStar,
  FaQuoteLeft,
  FaExternalLinkAlt,
  FaBrain,
  FaSearch,
  FaPalette,
  FaCogs,
  FaFlask,
  FaRocket
} from 'react-icons/fa';
import useIsMobile from '../hooks/useIsMobile';

// Hero Section
const HeroSection = styled.section`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
  overflow: hidden;
  background: 
    linear-gradient(135deg, #ffffff 0%, #f8f9ff 30%, #ffffff 70%, #f5f7ff 100%),
    radial-gradient(circle at 20% 80%, rgba(0, 122, 255, 0.05) 0%, transparent 50%),
    radial-gradient(circle at 80% 20%, rgba(255, 59, 48, 0.03) 0%, transparent 50%);
  perspective: 1000px;
  padding: ${props => props.theme.spacing.xxxl} 0;
`;

const HeroBackground = styled(motion.div)`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: 
    radial-gradient(circle at 30% 70%, rgba(0, 122, 255, 0.08) 0%, transparent 40%),
    radial-gradient(circle at 70% 30%, rgba(255, 59, 48, 0.05) 0%, transparent 40%);
`;

const HeroContainer = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: ${props => props.theme.spacing.xxxl};
  align-items: center;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.xxl};
    text-align: center;
  }
`;

const HeroContent = styled.div`
  z-index: 2;
  position: relative;
`;

const BackButton = styled(motion.button)`
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(0, 122, 255, 0.2);
  border-radius: ${props => props.theme.radius.large};
  padding: ${props => props.theme.spacing.md} ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.text};
  text-decoration: none;
  font-weight: 600;
  cursor: pointer;
  margin-bottom: 6rem;
  
  &:hover {
    background: rgba(0, 122, 255, 0.1);
    border-color: ${props => props.theme.colors.primary};
  }
`;

const ServiceCategory = styled(motion.span)`
  display: inline-block;
  background: linear-gradient(135deg, rgba(0, 122, 255, 0.1), rgba(88, 86, 214, 0.1));
  color: ${props => props.theme.colors.primary};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  border-radius: ${props => props.theme.radius.large};
  font-size: 0.9rem;
  font-weight: 600;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const ServiceTitle = styled(motion.h1)`
  font-size: clamp(2.5rem, 5vw, 4rem);
  font-weight: 900;
  margin-bottom: ${props => props.theme.spacing.lg};
  background: linear-gradient(135deg, 
    #1a1a1a 0%, 
    #007AFF 25%, 
    #5856D6 50%, 
    #FF3B30 75%, 
    #1a1a1a 100%);
  background-size: 400% 400%;
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  animation: gradientFlow 8s ease infinite;
  
  @keyframes gradientFlow {
    0%, 100% { background-position: 0% 50%; }
    50% { background-position: 100% 50%; }
  }
`;

const ServiceDescription = styled(motion.p)`
  font-size: 1.2rem;
  color: rgba(26, 26, 26, 0.8);
  line-height: 1.6;
  margin-bottom: ${props => props.theme.spacing.xl};
`;

const TechnologiesSection = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${props => props.theme.spacing.lg};
  margin-bottom: ${props => props.theme.spacing.xl};
  justify-content: center;
  
  @media (max-width: 576px) {
    justify-content: center;
    gap: ${props => props.theme.spacing.md};
  }
`;

const TechBadge = styled(motion.div)`
  background: linear-gradient(135deg, rgba(0, 122, 255, 0.1), rgba(88, 86, 214, 0.1));
  border: 1px solid rgba(0, 122, 255, 0.2);
  border-radius: ${props => props.theme.radius.large};
  padding: ${props => props.theme.spacing.sm} ${props => props.theme.spacing.lg};
  color: ${props => props.theme.colors.primary};
  font-weight: 600;
  font-size: 0.9rem;
  backdrop-filter: blur(20px);
  transition: all 0.3s ease;
  
  &:hover {
    background: linear-gradient(135deg, rgba(0, 122, 255, 0.2), rgba(88, 86, 214, 0.2));
    border-color: ${props => props.theme.colors.primary};
    transform: translateY(-2px);
  }
`;

const HeroVisual = styled(motion.div)`
  display: flex;
  align-items: center;
  justify-content: center;
  position: relative;
`;

const ServiceIcon = styled(motion.div)`
  width: 200px;
  height: 200px;
  background: linear-gradient(135deg, ${props => props.color1 || '#007AFF'}, ${props => props.color2 || '#5856D6'});
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 4rem;
  color: white;
  box-shadow: 0 20px 60px rgba(0, 122, 255, 0.3);
  position: relative;
  
  &::before {
    content: '';
    position: absolute;
    top: -20px;
    left: -20px;
    right: -20px;
    bottom: -20px;
    background: linear-gradient(135deg, ${props => props.color1 || '#007AFF'}, ${props => props.color2 || '#5856D6'});
    border-radius: 50%;
    z-index: -1;
    filter: blur(30px);
    opacity: 0.3;
  }
`;

// Content Sections
const ContentSection = styled.section`
  padding: ${props => props.theme.spacing.xxxl} 0;
  background: ${props => props.theme.colors.background};
`;

const Container = styled.div`
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 ${props => props.theme.spacing.lg};
`;

const SectionTitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  text-align: center;
  margin-bottom: ${props => props.theme.spacing.xxl};
  color: ${props => props.theme.colors.text};
`;

const FeatureGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${props => props.theme.spacing.xl};
  margin-bottom: ${props => props.theme.spacing.xxxl};
`;

const FeatureCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: ${props => props.theme.radius.large};
  padding: ${props => props.theme.spacing.xxl};
  text-align: center;
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${props => props.color1}, ${props => props.color2});
  }
`;

const FeatureIcon = styled.div`
  width: 80px;
  height: 80px;
  background: linear-gradient(135deg, ${props => props.color1}, ${props => props.color2});
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 2rem;
  color: white;
  margin: 0 auto ${props => props.theme.spacing.lg};
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.1);
`;

const FeatureTitle = styled.h3`
  font-size: 1.3rem;
  font-weight: 600;
  margin-bottom: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.text};
`;

const FeatureDescription = styled.p`
  color: rgba(26, 26, 26, 0.7);
  line-height: 1.6;
`;

const ProcessSection = styled.div`
  background: linear-gradient(135deg, 
    rgba(255, 255, 255, 0.9) 0%, 
    rgba(248, 249, 255, 0.9) 50%, 
    rgba(255, 255, 255, 0.9) 100%
  );
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: ${props => props.theme.radius.xlarge};
  padding: ${props => props.theme.spacing.xxxl};
  margin-bottom: ${props => props.theme.spacing.xxxl};
  position: relative;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: 
      radial-gradient(circle at 20% 80%, rgba(0, 122, 255, 0.03) 0%, transparent 50%),
      radial-gradient(circle at 80% 20%, rgba(255, 59, 48, 0.02) 0%, transparent 50%);
    pointer-events: none;
  }
`;

const ProcessGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${props => props.theme.spacing.xxl};
  position: relative;
  z-index: 1;
  
  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: ${props => props.theme.spacing.xl};
  }
`;

const ProcessStep = styled(motion.div)`
  background: rgba(255, 255, 255, 0.8);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.3);
  border-radius: ${props => props.theme.radius.large};
  padding: ${props => props.theme.spacing.xxl};
  text-align: center;
  position: relative;
  transition: all 0.4s ease;
  overflow: hidden;
  
  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    height: 4px;
    background: linear-gradient(90deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.primaryDark});
    opacity: 0;
    transition: opacity 0.3s ease;
  }
  
  &::after {
    content: '';
    position: absolute;
    top: 50%;
    right: -${props => props.theme.spacing.xl};
    transform: translateY(-50%);
    width: ${props => props.theme.spacing.xl};
    height: 2px;
    background: linear-gradient(90deg, ${props => props.theme.colors.primary}, transparent);
    opacity: 0.3;
    
    @media (max-width: 968px) {
      display: none;
    }
  }
  
  &:last-child::after {
    display: none;
  }
  
  /* Only enable hover effects on non-touch devices */
  @media (hover: hover) and (pointer: fine) {
    &:hover {
      transform: translateY(-8px);
      box-shadow: 0 20px 60px rgba(0, 122, 255, 0.15);
      border-color: rgba(0, 122, 255, 0.3);
      
      &::before {
        opacity: 1;
      }
    }
  }
`;

const StepIcon = styled.div`
  width: 50px;
  height: 50px;
  background: linear-gradient(135deg, rgba(0, 122, 255, 0.1), rgba(88, 86, 214, 0.1));
  border: 2px solid rgba(0, 122, 255, 0.2);
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.2rem;
  color: ${props => props.theme.colors.primary};
  margin: 0 auto ${props => props.theme.spacing.md};
  transition: all 0.3s ease;
  
  /* Only enable hover effects on non-touch devices */
  @media (hover: hover) and (pointer: fine) {
    ${ProcessStep}:hover & {
      background: linear-gradient(135deg, rgba(0, 122, 255, 0.2), rgba(88, 86, 214, 0.2));
      border-color: ${props => props.theme.colors.primary};
      transform: scale(1.1);
    }
  }
`;

const StepTitle = styled.h4`
  font-size: 1.3rem;
  font-weight: 700;
  margin-bottom: ${props => props.theme.spacing.md};
  color: ${props => props.theme.colors.text};
  background: linear-gradient(135deg, #1a1a1a, ${props => props.theme.colors.primary});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
`;

const StepDescription = styled.p`
  color: rgba(26, 26, 26, 0.7);
  font-size: 1rem;
  line-height: 1.6;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const StepProgress = styled.div`
  width: 100%;
  height: 4px;
  background: rgba(0, 122, 255, 0.1);
  border-radius: 2px;
  overflow: hidden;
  position: relative;
  
  &::after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    height: 100%;
    background: linear-gradient(90deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.primaryDark});
    width: ${props => (props.step + 1) * 20}%;
    border-radius: 2px;
    transition: width 0.8s ease;
  }
`;

const TestimonialSection = styled.div`
  background: rgba(255, 255, 255, 0.9);
  backdrop-filter: blur(20px);
  border: 1px solid rgba(255, 255, 255, 0.2);
  border-radius: ${props => props.theme.radius.xlarge};
  padding: ${props => props.theme.spacing.xxxl};
  text-align: center;
  position: relative;
`;

const QuoteIcon = styled.div`
  font-size: 3rem;
  color: ${props => props.theme.colors.primary};
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const TestimonialText = styled.p`
  font-size: 1.3rem;
  font-style: italic;
  color: ${props => props.theme.colors.text};
  line-height: 1.6;
  margin-bottom: ${props => props.theme.spacing.xl};
  max-width: 800px;
  margin-left: auto;
  margin-right: auto;
`;

const TestimonialAuthor = styled.div`
  font-weight: 600;
  color: ${props => props.theme.colors.text};
  margin-bottom: ${props => props.theme.spacing.sm};
`;

const TestimonialCompany = styled.div`
  color: rgba(26, 26, 26, 0.7);
  font-size: 0.9rem;
`;

const CTASection = styled.section`
  padding: ${props => props.theme.spacing.xxxl} 0;
  background: linear-gradient(135deg, ${props => props.theme.colors.primary}, ${props => props.theme.colors.primaryDark});
  color: white;
  text-align: center;
`;

const CTATitle = styled.h2`
  font-size: 2.5rem;
  font-weight: 700;
  margin-bottom: ${props => props.theme.spacing.lg};
`;

const CTADescription = styled.p`
  font-size: 1.2rem;
  margin-bottom: ${props => props.theme.spacing.xxl};
  max-width: 600px;
  margin-left: auto;
  margin-right: auto;
  opacity: 0.9;
`;

const CTAButton = styled(motion.button)`
  background: rgba(255, 255, 255, 0.2);
  backdrop-filter: blur(20px);
  border: 2px solid rgba(255, 255, 255, 0.3);
  border-radius: ${props => props.theme.radius.large};
  padding: ${props => props.theme.spacing.lg} ${props => props.theme.spacing.xxl};
  color: white;
  font-size: 1.1rem;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: ${props => props.theme.spacing.sm};
  transition: all 0.3s ease;
  
  &:hover {
    background: rgba(255, 255, 255, 0.3);
    border-color: rgba(255, 255, 255, 0.5);
    transform: translateY(-2px);
  }
`;

const ServiceDetail = ({ setCursorVariant }) => {
  const { serviceId } = useParams();
  const heroRef = useRef(null);
  const isMobile = useIsMobile();
  
  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"]
  });

  const parallaxY = useTransform(scrollYProgress, [0, 1], [0, -200]);

  const services = {
    'sviluppo-web': {
      title: 'Sviluppo Web Avanzato',
      category: 'Sviluppo Web',
      description: 'Creiamo siti web e applicazioni web moderne, responsive e performanti utilizzando le tecnologie più avanzate del settore.',
      icon: FaCode,
      color1: '#007AFF',
      color2: '#5856D6',
      technologies: ['React', 'Vue.js', 'Node.js', 'Python', 'TypeScript', 'GraphQL'],
      features: [
        {
          icon: FaDesktop,
          title: 'Design Responsive',
          description: 'Siti web che si adattano perfettamente a tutti i dispositivi e schermi.',
          color1: '#007AFF',
          color2: '#5856D6'
        },
        {
          icon: FaRobot,
          title: 'Ottimizzazione SEO',
          description: 'Miglioriamo la visibilità del tuo sito sui motori di ricerca.',
          color1: '#30D158',
          color2: '#32D74B'
        },
        {
          icon: FaCheck,
          title: 'Performance Ottimali',
          description: 'Caricamento veloce e prestazioni eccellenti per una migliore user experience.',
          color1: '#FF3B30',
          color2: '#FF9500'
        }
      ],
      process: [
        { title: 'Analisi', description: 'Studio approfondito dei requisiti e degli obiettivi', icon: FaSearch },
        { title: 'Design', description: 'Creazione di mockup e prototipi interattivi', icon: FaPalette },
        { title: 'Sviluppo', description: 'Implementazione con tecnologie moderne', icon: FaCogs },
        { title: 'Testing', description: 'Test accurati su tutti i dispositivi', icon: FaFlask },
        { title: 'Lancio', description: 'Deploy e monitoraggio delle performance', icon: FaRocket }
      ]
    },
    'app-mobile': {
      title: 'Sviluppo App Mobile',
      category: 'Mobile Development',
      description: 'Sviluppiamo app mobile native e cross-platform per iOS e Android con design intuitivo e funzionalità avanzate.',
      icon: FaMobile,
      color1: '#30D158',
      color2: '#32D74B',
      technologies: ['React Native', 'Flutter', 'Swift', 'Kotlin', 'Firebase', 'Redux'],
      features: [
        {
          icon: FaMobile,
          title: 'Cross-Platform',
          description: 'Un codice per iOS e Android, riducendo tempi e costi di sviluppo.',
          color1: '#30D158',
          color2: '#32D74B'
        },
        {
          icon: FaCheck,
          title: 'UI/UX Intuitiva',
          description: 'Design centrato sull\'utente per la massima usabilità.',
          color1: '#FF3B30',
          color2: '#FF9500'
        },
        {
          icon: FaRobot,
          title: 'Integrazione AI',
          description: 'Funzionalità intelligenti per un\'esperienza utente superiore.',
          color1: '#FFD60A',
          color2: '#FFCC02'
        }
      ],
      process: [
        { title: 'Concept', description: 'Definizione dell\'idea e target di riferimento', icon: FaSearch },
        { title: 'Prototipo', description: 'Creazione di wireframe e mockup interattivi', icon: FaPalette },
        { title: 'Sviluppo', description: 'Codifica con framework moderni', icon: FaCogs },
        { title: 'Testing', description: 'Test su dispositivi reali e beta testing', icon: FaFlask },
        { title: 'Pubblicazione', description: 'Rilascio sugli store ufficiali', icon: FaRocket }
      ]
    },
    'realta-virtuale': {
      title: 'Realtà Virtuale e Aumentata',
      category: 'VR/AR Development',
      description: 'Creiamo esperienze immersive in realtà virtuale e aumentata per formazione, intrattenimento e business.',
      icon: FaVrCardboard,
      color1: '#FF3B30',
      color2: '#FF9500',
      technologies: ['Unity 3D', 'Unreal Engine', 'Meta Quest', 'ARCore', 'ARKit', 'WebXR'],
      features: [
        {
          icon: FaVrCardboard,
          title: 'VR Immersiva',
          description: 'Esperienze di realtà virtuale coinvolgenti e realistiche.',
          color1: '#FF3B30',
          color2: '#FF9500'
        },
        {
          icon: FaMobile,
          title: 'AR Mobile',
          description: 'Applicazioni di realtà aumentata per smartphone e tablet.',
          color1: '#007AFF',
          color2: '#5856D6'
        },
        {
          icon: FaGraduationCap,
          title: 'Training VR',
          description: 'Soluzioni educative e di formazione in realtà virtuale.',
          color1: '#30D158',
          color2: '#32D74B'
        }
      ],
      process: [
        { title: 'Concezione', description: 'Definizione dell\'esperienza e obiettivi', icon: FaSearch },
        { title: 'Storyboard', description: 'Progettazione del flusso narrativo', icon: FaPalette },
        { title: 'Modellazione 3D', description: 'Creazione di asset e ambienti virtuali', icon: FaCogs },
        { title: 'Sviluppo', description: 'Implementazione con Unity/Unreal Engine', icon: FaFlask },
        { title: 'Deploy', description: 'Ottimizzazione e distribuzione', icon: FaRocket }
      ]
    },
    'intelligenza-artificiale': {
      title: 'Intelligenza Artificiale',
      category: 'AI Development',
      description: 'Sviluppiamo soluzioni di intelligenza artificiale personalizzate per automatizzare processi, analizzare dati e migliorare l\'efficienza aziendale.',
      icon: FaBrain,
      color1: '#FFD60A',
      color2: '#FFCC02',
      technologies: ['TensorFlow', 'PyTorch', 'Python', 'OpenAI', 'scikit-learn', 'Keras'],
      features: [
        {
          icon: FaBrain,
          title: 'Machine Learning',
          description: 'Algoritmi di apprendimento automatico per predizioni accurate e insights intelligenti.',
          color1: '#FFD60A',
          color2: '#FFCC02'
        },
        {
          icon: FaRobot,
          title: 'Automazione Intelligente',
          description: 'Automazione dei processi con intelligenza artificiale per massimizzare l\'efficienza.',
          color1: '#30D158',
          color2: '#32D74B'
        },
        {
          icon: FaDesktop,
          title: 'Analisi Dati Avanzata',
          description: 'Elaborazione e analisi di grandi quantità di dati per decisioni strategiche.',
          color1: '#007AFF',
          color2: '#5856D6'
        }
      ],
      process: [
        { title: 'Analisi Dati', description: 'Valutazione delle fonti dati e definizione degli obiettivi', icon: FaSearch },
        { title: 'Progettazione', description: 'Creazione dell\'architettura AI e selezione algoritmi', icon: FaPalette },
        { title: 'Training', description: 'Addestramento dei modelli con dataset specifici', icon: FaCogs },
        { title: 'Validazione', description: 'Test e validazione delle performance del modello', icon: FaFlask },
        { title: 'Implementazione', description: 'Integrazione in produzione e monitoraggio continuo', icon: FaRocket }
      ]
    }
  };

  const currentService = services[serviceId];

  useEffect(() => {
    document.title = `${currentService?.title || 'Servizio'} | D-Soft`;
    // Scroll to top only when the component first mounts
    window.scrollTo(0, 0);
  }, [serviceId]); // Only depend on serviceId, not currentService

  if (!currentService) {
    return (
      <ContentSection>
        <Container>
          <div style={{ textAlign: 'center', padding: '4rem 0' }}>
            <h1>Servizio Non Trovato</h1>
            <p>Il servizio richiesto non esiste.</p>
            <Link to="/servizi">
              <CTAButton as="div" style={{ marginTop: '2rem', display: 'inline-flex' }}>
                <FaArrowLeft />
                Torna ai Servizi
              </CTAButton>
            </Link>
          </div>
        </Container>
      </ContentSection>
    );
  }

  return (
    <div>
      {/* Hero Section */}
      <HeroSection ref={heroRef}>
        <HeroBackground style={{ y: parallaxY }} />
        
        <HeroContainer>
          <HeroContent>
            <Link to="/servizi">
              <BackButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onMouseEnter={() => setCursorVariant("button")}
                onMouseLeave={() => setCursorVariant("default")}
              >
                <FaArrowLeft />
                Torna ai Servizi
              </BackButton>
            </Link>
            
            <ServiceCategory
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.6 }}
            >
              {currentService.category}
            </ServiceCategory>
            
            <ServiceTitle
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.2 }}
              onMouseEnter={() => setCursorVariant("text")}
              onMouseLeave={() => setCursorVariant("default")}
            >
              {currentService.title}
            </ServiceTitle>
            
            <ServiceDescription
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.4 }}
            >
              {currentService.description}
            </ServiceDescription>
            
            <TechnologiesSection>
              {currentService.technologies.map((tech, index) => (
                <TechBadge
                  key={index}
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: isMobile ? 0.4 : 0.6, delay: isMobile ? 0 : 0.6 + index * 0.1 }}
                  whileHover={isMobile ? {} : { scale: 1.05 }}
                  onMouseEnter={isMobile ? undefined : () => setCursorVariant("link")}
                  onMouseLeave={isMobile ? undefined : () => setCursorVariant("default")}
                >
                  {tech}
                </TechBadge>
              ))}
            </TechnologiesSection>
          </HeroContent>
          
          <HeroVisual>
            <ServiceIcon
              color1={currentService.color1}
              color2={currentService.color2}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: isMobile ? 0.6 : 1, delay: isMobile ? 0.4 : 0.8 }}
              whileHover={isMobile ? {} : { scale: 1.1, rotate: 5 }}
            >
              <currentService.icon />
            </ServiceIcon>
          </HeroVisual>
        </HeroContainer>
      </HeroSection>

      {/* Features Section */}
      <ContentSection>
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <SectionTitle
              onMouseEnter={() => setCursorVariant("text")}
              onMouseLeave={() => setCursorVariant("default")}
            >
              Caratteristiche Principali
            </SectionTitle>
          </motion.div>
          
          <FeatureGrid>
            {currentService.features.map((feature, index) => (
              <FeatureCard
                key={index}
                color1={feature.color1}
                color2={feature.color2}
                initial={{ opacity: 0, y: isMobile ? 30 : 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: isMobile ? 0.4 : 0.6, delay: isMobile ? 0 : index * 0.2 }}
                whileHover={isMobile ? {} : { scale: 1.05, y: -10 }}
                onMouseEnter={isMobile ? undefined : () => setCursorVariant("link")}
                onMouseLeave={isMobile ? undefined : () => setCursorVariant("default")}
              >
                <FeatureIcon color1={feature.color1} color2={feature.color2}>
                  <feature.icon />
                </FeatureIcon>
                <FeatureTitle>{feature.title}</FeatureTitle>
                <FeatureDescription>{feature.description}</FeatureDescription>
              </FeatureCard>
            ))}
          </FeatureGrid>

          {/* Process Section */}
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <ProcessSection>
              <SectionTitle
                onMouseEnter={() => setCursorVariant("text")}
                onMouseLeave={() => setCursorVariant("default")}
              >
                Il Nostro Processo
              </SectionTitle>
              
              <ProcessGrid>
                {currentService.process.map((step, index) => (
                  <ProcessStep
                    key={index}
                    initial={{ opacity: 0, y: isMobile ? 20 : 30 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: isMobile ? 0.4 : 0.6, delay: isMobile ? 0 : index * 0.1 }}
                    whileHover={isMobile ? {} : { scale: 1.02 }}
                    onMouseEnter={isMobile ? undefined : () => setCursorVariant("link")}
                    onMouseLeave={isMobile ? undefined : () => setCursorVariant("default")}
                  >
                    <StepIcon>
                      <step.icon />
                    </StepIcon>
                    <StepTitle>{step.title}</StepTitle>
                    <StepDescription>{step.description}</StepDescription>
                    <StepProgress step={index} />
                  </ProcessStep>
                ))}
              </ProcessGrid>
            </ProcessSection>
          </motion.div>
        </Container>
      </ContentSection>

      {/* CTA Section */}
      <CTASection>
        <Container>
          <motion.div
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.8 }}
          >
            <CTATitle
              onMouseEnter={() => setCursorVariant("text")}
              onMouseLeave={() => setCursorVariant("default")}
            >
              Pronto a Iniziare?
            </CTATitle>
            <CTADescription>
              Contattaci oggi per discutere il tuo progetto e scoprire come possiamo aiutarti a raggiungere i tuoi obiettivi.
            </CTADescription>
            <Link to="/contatti">
              <CTAButton
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                onMouseEnter={() => setCursorVariant("button")}
                onMouseLeave={() => setCursorVariant("default")}
              >
                Contattaci Ora
                <FaArrowRight />
              </CTAButton>
            </Link>
          </motion.div>
        </Container>
      </CTASection>
    </div>
  );
};

export default ServiceDetail; 